import { useEffect, useRef, useState } from 'react';

const POLL_MS = 2 * 60 * 1000;

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.version === 'string' ? data.version : null;
  } catch {
    return null;
  }
}

export function useVersionCheck(): { hasUpdate: boolean } {
  const [hasUpdate, setHasUpdate] = useState(false);
  const initialRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const v = await fetchVersion();
      if (cancelled || !v) return;
      if (initialRef.current === null) {
        initialRef.current = v;
        return;
      }
      if (v !== initialRef.current) {
        setHasUpdate(true);
      }
    };

    tick();
    const id = window.setInterval(tick, POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { hasUpdate };
}
